import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  DayOfWeek,
  OccasionType,
  UnifiedHoursState,
  DayScheduleV2,
  SpecialHoursEntryV2,
} from './types';
import { UnifiedHoursEditor } from './UnifiedHoursEditor';

interface SingleStoreHoursDialogProps {
  open: boolean;
  onClose: () => void;
  storeNumber: string;
  storeName: string;
  // Existing hours data for all occasions
  existingHours?: Record<
    OccasionType,
    {
      enabled: boolean;
      schedule: Record<DayOfWeek, DayScheduleV2>;
    }
  >;
  // Existing special hours for each occasion
  existingSpecialHours?: Record<OccasionType, SpecialHoursEntryV2[]>;
  // Available occasions for this store
  availableOccasions: OccasionType[];
  // Callback when saving
  onSave: (data: {
    occasions: Record<
      OccasionType,
      {
        enabled: boolean;
        schedule: Record<DayOfWeek, DayScheduleV2>;
      }
    >;
    specialHours?: Record<OccasionType, SpecialHoursEntryV2[]>;
  }) => Promise<void>;
}

export const SingleStoreHoursDialog: React.FC<SingleStoreHoursDialogProps> = ({
  open,
  onClose,
  storeNumber,
  storeName,
  existingHours,
  existingSpecialHours,
  availableOccasions,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [unifiedHoursState, setUnifiedHoursState] = useState<UnifiedHoursState | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(false);
      setUnifiedHoursState(null);
    }
  }, [open]);

  const handleSave = async () => {
    if (!unifiedHoursState) {
      setError('No changes to save');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Convert unified state to the format expected by onSave
      const occasionsData: Record<
        OccasionType,
        {
          enabled: boolean;
          schedule: Record<DayOfWeek, DayScheduleV2>;
        }
      > = {} as any;

      Object.entries(unifiedHoursState.occasionSettings).forEach(([occ, settings]) => {
        const occasionKey = occ as OccasionType;
        // For inherited occasions, use primary schedule; for override, use custom
        const schedule =
          settings.mode === 'inherit' || !settings.customSchedule
            ? unifiedHoursState.primarySchedule
            : settings.customSchedule;

        occasionsData[occasionKey] = {
          enabled: true,
          schedule,
        };
      });

      await onSave({
        occasions: occasionsData,
        specialHours: unifiedHoursState.specialHours,
      });

      // Show success state
      setSuccess(true);
      setLoading(false);

      // Auto-close after 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to save hours:', err);
      setError(err instanceof Error ? err.message : 'Failed to save hours');
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ScheduleIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6">Manage All Hours</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
              <Chip label={storeNumber} size="small" sx={{ fontWeight: 'bold' }} />
              <Typography variant="body2" color="text.secondary">
                {storeName}
              </Typography>
            </Stack>
          </Box>
          <IconButton onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      {success ? (
        // Success state
        <DialogContent sx={{ p: 6 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 64,
                color: 'success.main',
                mb: 2,
              }}
            />
            <Typography variant="h5" fontWeight={600} color="success.main">
              Hours Saved!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              All occasions have been updated
            </Typography>
          </Box>
        </DialogContent>
      ) : (
        // Normal edit state
        <>
          <DialogContent dividers sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 2 }}>
              Click & Collect and Delivery hours are saved separately. In Manage All Hours, Delivery
              copies Click & Collect unless you set it to Override.
            </Alert>

            <UnifiedHoursEditor
              availableOccasions={availableOccasions}
              initialHours={existingHours}
              initialSpecialHours={existingSpecialHours}
              onChange={setUnifiedHoursState}
              showTemplates={true}
              showSpecialHours={false}
            />
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading || !unifiedHoursState}
            >
              {loading ? 'Saving...' : 'Save All Hours'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default SingleStoreHoursDialog;
